/**
 * Round a monetary amount to 2 decimals (centimes), half away from zero.
 * Decimal-string shift dodges binary float artifacts:
 *   389.15999999999997 -> 389.16, 1.005 -> 1.01
 */
export function roundCurrency(value: number): number {
  if (!Number.isFinite(value)) return value;
  if (value < 0) return -roundCurrency(-value);
  const shifted = Math.round(Number(`${value}e2`));
  return Number.isNaN(shifted)
    ? Math.round(value * 100) / 100 // exponential-notation fallback
    : Number(`${shifted}e-2`);
}

export function roundCurrencyOptional(
  value: number | undefined,
): number | undefined {
  return value === undefined ? undefined : roundCurrency(value);
}

export interface BillTotalsItem {
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

export interface BillTotals {
  subtotal: number;
  vatAmount: number;
  total: number;
}

/**
 * How the VAT of an invoice is derived from its lines, shipping and discount.
 *
 * - 1 (legacy): VAT on the line totals only; shipping and discount enter the
 *   total but not the VAT base. Kept to re-read invoices issued that way —
 *   art. 27 al. 2 LTVA: the VAT printed on the invoice is the VAT owed.
 * - 2 (current, `bills.totalsVersion`): shipping and discount are allocated
 *   pro rata across the rate buckets (art. 24 al. 1 LTVA), so the VAT base
 *   of the declaration and the invoice agree.
 */
export type BillTotalsVersion = 1 | 2;
export const CURRENT_TOTALS_VERSION: BillTotalsVersion = 2;

/**
 * Line totals are rounded individually, then summed, so stored/displayed
 * line items always add up exactly to the subtotal.
 */
export function calculateBillTotals(
  items: BillTotalsItem[],
  shippingCosts: number,
  discount: number,
  version: BillTotalsVersion = CURRENT_TOTALS_VERSION,
): BillTotals {
  let subtotal = 0;
  let vatAmount = 0;
  for (const item of items) {
    const lineTotal = roundCurrency(item.quantity * item.unitPrice);
    subtotal += lineTotal;
    vatAmount += lineTotal * (item.vatRate / 100);
  }
  subtotal = roundCurrency(subtotal);
  if (version === 2 && (shippingCosts !== 0 || discount !== 0)) {
    vatAmount = vatOnAllocatedBase(items, shippingCosts, discount);
  } else {
    vatAmount = roundCurrency(vatAmount);
  }
  const total = roundCurrency(subtotal + shippingCosts - discount + vatAmount);
  return { subtotal, vatAmount, total };
}

/**
 * Inlined twin of `billVatBuckets` + `vatFromBuckets` (shared/finance/
 * vatBuckets.ts imports this module, so the dependency cannot go the other
 * way). The bucket test pins both to the same figures.
 */
function vatOnAllocatedBase(
  items: BillTotalsItem[],
  shippingCosts: number,
  discount: number,
): number {
  const buckets = new Map<number, number>();
  let subtotal = 0;
  for (const item of items) {
    const lineTotal = roundCurrency(item.quantity * item.unitPrice);
    subtotal += lineTotal;
    buckets.set(item.vatRate, (buckets.get(item.vatRate) ?? 0) + lineTotal);
  }
  subtotal = roundCurrency(subtotal);
  const adj = roundCurrency(shippingCosts - discount);
  if (subtotal === 0) return 0;
  const factor = 1 + adj / subtotal;
  let allocated = 0;
  let largestRate = 0;
  let largestBase = -Infinity;
  for (const [rate, base] of buckets) {
    const adjusted = roundCurrency(base * factor);
    buckets.set(rate, adjusted);
    allocated += adjusted;
    if (base > largestBase) {
      largestBase = base;
      largestRate = rate;
    }
  }
  const residual = roundCurrency(subtotal + adj) - roundCurrency(allocated);
  if (residual !== 0) {
    buckets.set(
      largestRate,
      roundCurrency((buckets.get(largestRate) ?? 0) + residual),
    );
  }
  let vat = 0;
  for (const [rate, base] of buckets) vat += roundCurrency((base * rate) / 100);
  return roundCurrency(vat);
}
